import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Globe, Wand2, Check, X as XIcon, Plus, Hash, Image as ImageIcon, ArrowLeft } from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';
import { useAuth } from '../contexts/AuthContext';
import { useSeoAndHashtags } from '../hooks/useSeoAndHashtags';
import { useToast } from './ui/Toast';
import { 
  ProductData, 
  ProductImage, 
  Platform, 
  getConnectedPlatforms,
  generateCaptionWithAI,
  publishProducts
} from '../services/massPublishingService';
import PublicarConIA from './PublicarConIA';
import { ProductoConImagen } from '../hooks/useFetchStockData';
import { renderMultiValueField, formatValue, isMultiValueField } from '../utils/fieldDisplayUtils';

interface MassPublishingSocialModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const MassPublishingSocialModal: React.FC<MassPublishingSocialModalProps> = ({ isOpen, onClose }) => {
  const { t } = useI18n();
  const { user } = useAuth();
  const { hashtags, predefinedHashtags } = useSeoAndHashtags();
  const { showWarning, showSuccess, ToastContainer } = useToast();
  const [productImages, setProductImages] = useState<ProductImage[]>([]);
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [loading, setLoading] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [publishing, setPublishing] = useState(false);
  
  // Estados para el nuevo sistema de Publicar con IA
  const [showPublicarConIA, setShowPublicarConIA] = useState(false);
  const [selectedProducto, setSelectedProducto] = useState<ProductoConImagen | null>(null);
  
  // Estados para formularios
  const [customHashtags, setCustomHashtags] = useState<string[]>([]);
  const [newHashtag, setNewHashtag] = useState('');
  const [aiGeneratedCaption, setAiGeneratedCaption] = useState('');

  // Cargar datos iniciales
  useEffect(() => {
    if (isOpen && user) {
      loadInitialData();
    }
  }, [isOpen, user]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      // Primero cargar las licencias conectadas de redes sociales
      const { db, collection, getDocs } = window.auth;
      const socialLicensesRef = collection(db, 'users', user!.uid, 'licenses', 'redes');
      const socialSnapshot = await getDocs(socialLicensesRef);
      
      const socialLicenses: string[] = [];
      socialSnapshot.forEach((doc: any) => {
        socialLicenses.push(doc.id);
      });
      
      console.log('✅ Licencias de redes sociales:', socialLicenses);
      
      // Si hay licencias, cargar las plataformas y filtrar por las que tienen licencia
      if (socialLicenses.length > 0) {
        const platformsData = await getConnectedPlatforms(user!.uid);
        const socialPlatforms = platformsData.filter(p => p.type === 'social');
        
        // Filtrar solo las plataformas que tienen licencia conectada
        const filteredPlatforms = socialPlatforms.filter(p => 
          socialLicenses.includes(p.id.toLowerCase())
        );
        
        console.log('✅ Plataformas sociales con licencia:', filteredPlatforms.map(p => p.id));
        setPlatforms(filteredPlatforms);
      } else {
        // Si no hay licencias, no mostrar plataformas
        console.log('⚠️ No hay plataformas sociales con licencia');
        setPlatforms([]);
      }
      
    } catch (error) {
      console.error('Error cargando datos iniciales:', error);
      // En caso de error, no mostrar plataformas
      setPlatforms([]);
    } finally {
      setLoading(false);
    }
  };

  // Manejar selección de producto desde PublicarConIA
  const handleProductSelect = async (producto: ProductoConImagen) => {
    setSelectedProducto(producto);
    setShowPublicarConIA(false);
    
    // Convertir ProductoConImagen a ProductData para compatibilidad
    const productData: ProductData = {
      id: producto.sku,
      name: producto.sku,
      sku: producto.sku,
      description: `${producto.marca} - ${producto.genero} - ${producto.temporada}`,
      price: producto.precioVenta,
      stock: producto.stock,
      category: 'Seleccionado',
      createdAt: new Date(),
      updatedAt: new Date(),
      path: producto.imagenPath || ''
    };
    
    // Cargar todas las imágenes del producto desde Firebase Storage
    await loadProductImages(producto);
  };

  // Función para cargar todas las imágenes de un producto desde Firebase Storage
  const loadProductImages = async (producto: ProductoConImagen) => {
    if (!user || !window.auth?.storage) {
      console.warn('Firebase Storage no disponible para cargar imágenes');
      return;
    }

    try {
      console.log('🖼️ Cargando imágenes para producto:', producto.sku);
      
      // Buscar imágenes en diferentes rutas posibles
      const possiblePaths = [
        `users/${user.uid}/categories/Remera/stock/${producto.sku}`,
        `users/${user.uid}/categories/remera/stock/${producto.sku}`,
        `users/${user.uid}/categories/${producto.categoria || 'Remera'}/stock/${producto.sku}`
      ];

      const images: ProductImage[] = [];
      
      for (const basePath of possiblePaths) {
        try {
          const storage = window.auth.storage;
          const listRef = window.auth.ref(storage, basePath);
          const res = await window.auth.listAll(listRef);
          
          console.log(`🔍 Buscando imágenes en: ${basePath}`);
          console.log(`📁 Archivos encontrados:`, res.items.map((item: any) => item.name));
          
          for (const item of res.items) {
            try {
              const url = await window.auth.getDownloadURL(item);
              const imageName = item.name;
              
              // Determinar el tipo de imagen basado en el nombre
              let imageType: 'front' | 'back' | 'closeup' | 'variant' = 'front';
              if (imageName.toLowerCase().includes('back') || imageName.toLowerCase().includes('reverso')) {
                imageType = 'back';
              } else if (imageName.toLowerCase().includes('side') || imageName.toLowerCase().includes('lateral')) {
                imageType = 'variant';
              } else if (imageName.toLowerCase().includes('detail') || imageName.toLowerCase().includes('detalle')) {
                imageType = 'closeup';
              }
              
              images.push({
                id: `${producto.sku}-${imageName}`,
                url: url,
                name: imageName,
                type: imageType,
                selected: true // Por defecto todas seleccionadas
              });
              
              console.log(`✅ Imagen cargada: ${imageName} (${imageType})`);
            } catch (urlError) {
              console.warn(`⚠️ Error obteniendo URL para ${item.name}:`, urlError);
            }
          }
          
          // Si encontramos imágenes en esta ruta, no buscar en las demás
          if (images.length > 0) {
            break;
          }
        } catch (pathError) {
          console.log(`🔍 No se encontraron imágenes en: ${basePath}`);
        }
      }
      
      // Si no encontramos imágenes en Storage, usar la imagen principal del producto si existe
      if (images.length === 0 && producto.imagenUrl) {
        console.log('🖼️ Usando imagen principal del producto');
        images.push({
          id: `${producto.sku}-main`,
          url: producto.imagenUrl,
          name: `${producto.sku}-main.jpg`,
          type: 'front',
          selected: true
        });
      }
      
      console.log(`🖼️ Total imágenes cargadas: ${images.length}`);
      setProductImages(images);
      
    } catch (error) {
      console.error('❌ Error cargando imágenes del producto:', error);
      // En caso de error, usar imagen principal si existe
      if (producto.imagenUrl) {
        setProductImages([{
          id: `${producto.sku}-main`,
          url: producto.imagenUrl,
          name: `${producto.sku}-main.jpg`,
          type: 'front',
          selected: true
        }]);
      }
    }
  };

  // Manejar selección/deselección de imágenes
  const toggleImageSelection = (imageId: string) => {
    setProductImages(prev => 
      prev.map(img => 
        img.id === imageId 
          ? { ...img, selected: !img.selected }
          : img
      )
    );
  };

  // Manejar generación con IA
  const handleGenerateWithAI = async (producto: ProductoConImagen) => {
    setGeneratingAI(true);
    try {
      const productData: ProductData = {
        id: producto.sku,
        name: producto.sku,
        sku: producto.sku,
        description: `${producto.marca} - ${producto.genero} - ${producto.temporada}`,
        price: producto.precioVenta,
        stock: producto.stock,
        category: 'Seleccionado',
        createdAt: new Date(),
        updatedAt: new Date(),
        path: producto.imagenPath || ''
      };
      
      const caption = await generateCaptionWithAI(productData, customHashtags);
      setAiGeneratedCaption(caption);
      } catch (error) {
      console.error('Error generando caption:', error);
      } finally {
      setGeneratingAI(false);
    }
  };

  // Manejar publicación
  const handlePublish = async (producto: ProductoConImagen) => {
    const selectedPlatforms = platforms.filter(p => p.selected);
    
    if (selectedPlatforms.length === 0) {
      showWarning(
        'No hay redes sociales seleccionadas',
        'Selecciona al menos una red social para continuar con la publicación.'
      );
      return;
    }

    setPublishing(true);
    try {
      const productData: ProductData = {
        id: producto.sku,
        name: producto.sku,
        sku: producto.sku,
        description: aiGeneratedCaption || `${producto.marca} - ${producto.genero} - ${producto.temporada}`,
        price: producto.precioVenta,
        stock: producto.stock,
        category: 'Seleccionado',
        createdAt: new Date(),
        updatedAt: new Date(),
        path: producto.imagenPath || ''
      };
      
      const selectedImages = productImages.filter(img => img.selected);
      
      await publishProducts([productData], selectedPlatforms, {
        images: selectedImages,
        hashtags: customHashtags,
        aiCaption: aiGeneratedCaption
      });
      
      showSuccess(
        'Publicación exitosa',
        `Contenido publicado en ${selectedPlatforms.length} red${selectedPlatforms.length !== 1 ? 'es' : ''} social${selectedPlatforms.length !== 1 ? 'es' : ''}.`
      );
      
      onClose();
    } catch (error) {
      console.error('Error en la publicación:', error);
    } finally {
      setPublishing(false);
    }
  };

  // Funciones para manejar hashtags
  const handleHashtagAdd = (hashtag: string) => {
    if (!customHashtags.includes(hashtag)) {
      setCustomHashtags(prev => [...prev, hashtag]);
    }
  };

  const handleCustomHashtagAdd = () => {
    if (newHashtag.trim() && !customHashtags.includes(newHashtag.trim())) {
      setCustomHashtags(prev => [...prev, newHashtag.trim()]);
      setNewHashtag('');
    }
  };

  const handleHashtagRemove = (hashtag: string) => {
    setCustomHashtags(prev => prev.filter(h => h !== hashtag));
  };

  const handlePlatformToggle = (platformId: string) => {
    setPlatforms(prev => 
      prev.map(p => 
        p.id === platformId ? { ...p, selected: !p.selected } : p
      )
    );
  };

  const handleImageToggle = (imageId: string) => {
    setProductImages(prev => 
      prev.map(img => 
        img.id === imageId ? { ...img, selected: !img.selected } : img
      )
    );
  };

  const selectedPlatformsCount = platforms.filter(p => p.selected).length;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Modal principal */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-6 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-pink-100 dark:bg-pink-900/30 rounded-xl">
                  <Globe className="w-6 h-6 text-pink-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    Publicar en Mis Redes
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Publica contenido en tus redes sociales conectadas
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-xl text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors duration-200"
              >
                <X size={24} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                    <span className="ml-3 text-gray-600 dark:text-gray-300">{t('loadingPlatforms')}</span>
                </div>
              ) : (
                  <div className="space-y-6">
                    {/* Botón para elegir producto - solo si no hay producto seleccionado */}
                    {!selectedProducto && (
                      <div className="text-center py-8">
                        <div className="w-20 h-20 bg-gradient-to-r from-pink-500 to-rose-500 rounded-full flex items-center justify-center mx-auto mb-4">
                          <ImageIcon className="w-10 h-10 text-white" />
                      </div>
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                          Selecciona un producto para publicar
                        </h3>
                        <p className="text-gray-600 dark:text-gray-300 mb-6">
                          Usa nuestro sistema inteligente para cargar productos y generar contenido con IA
                            </p>
                            <button
                          onClick={() => setShowPublicarConIA(true)}
                          className="px-6 py-3 bg-gradient-to-r from-pink-600 to-rose-600 text-white rounded-lg hover:from-pink-700 hover:to-rose-700 transition-all duration-200 font-medium"
                            >
                          <ImageIcon className="w-5 h-5 inline mr-2" />
                          Elegir Producto
                            </button>
                      </div>
                    )}

                    {/* Selección de imágenes del producto - solo si hay producto seleccionado */}
                    {selectedProducto && (
                      <div className="space-y-6">
                        <div className="text-center">
                          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                            Selecciona las imágenes para publicar
                        </h3>
                          <p className="text-gray-600 dark:text-gray-300">
                            Elige qué imágenes del producto {selectedProducto.sku} quieres incluir en la publicación
                                      </p>
                                    </div>

                        {/* Información de selección - ARRIBA */}
                        <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                Imágenes seleccionadas: {productImages.filter(img => img.selected).length} de {productImages.length}
                              </p>
                              <p className="text-xs text-gray-600 dark:text-gray-300">
                                Selecciona al menos una imagen para continuar
                              </p>
                            </div>
                          <button
                              onClick={() => setSelectedProducto(null)}
                              className="px-3 py-1 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 transition-colors"
                            >
                              Cambiar producto
                          </button>
                          </div>
                        </div>

                        {/* Grid de imágenes - ABAJO */}
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                          {productImages.map((image) => (
                            <div
                              key={image.id}
                              className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                                image.selected
                                  ? 'border-primary-500 ring-2 ring-primary-200'
                                  : 'border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500'
                              }`}
                              onClick={() => toggleImageSelection(image.id)}
                            >
                              <img
                                src={image.url}
                                alt={image.name}
                                className="w-full h-32 object-cover"
                              />
                              <div className="absolute top-2 right-2">
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                                  image.selected
                                    ? 'bg-primary-500 text-white'
                                    : 'bg-white dark:bg-slate-800 text-gray-400 border border-gray-300 dark:border-slate-600'
                                }`}>
                                  {image.selected && <Check className="w-4 h-4" />}
                                </div>
                                  </div>
                              <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs p-2">
                                <div className="truncate">{image.name}</div>
                                <div className="text-gray-300 capitalize">{image.type}</div>
                              </div>
                              </div>
                          ))}
                        </div>

                        {/* Grid de imágenes generadas con IA */}
                        <div className="border-t border-gray-200 dark:border-slate-600 pt-6">
                          <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                            Imágenes generadas con IA
                          </h4>
                          
                          {false ? ( // Cambiar esto cuando haya imágenes generadas
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                              {/* Aquí irán las imágenes generadas con IA */}
                            </div>
                          ) : (
                            <div className="bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-xl p-8 border border-purple-200 dark:border-purple-800">
                              <div className="text-center">
                                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full mb-4">
                                  <Wand2 className="w-8 h-8 text-white" />
                                </div>
                                <h5 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                                  Crea imágenes con IA
                                </h5>
                                <p className="text-xs text-purple-600 dark:text-purple-400 font-medium">
                                  ✨ Descubre nuevas posibilidades creativas
                                </p>
                              </div>
                            </div>
                          )}

                          {/* Botón de generar/regenerar con IA */}
                          <div className="flex justify-center mt-6">
                            <button
                              onClick={() => {
                                console.log('Generar imagen con IA - Función pendiente de implementar');
                              }}
                              className="px-6 py-2 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-lg hover:from-purple-600 hover:to-blue-600 transition-all duration-200 font-medium shadow-md hover:shadow-lg"
                            >
                              <div className="flex items-center space-x-2">
                                <Wand2 className="w-5 h-5" />
                                <span>{t('generateRegenerateImageWithAI')}</span>
                              </div>
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Información del producto seleccionado */}
                    {selectedProducto && (
                      <div className="bg-gray-50 dark:bg-slate-800 rounded-xl p-6">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                          {selectedProducto.nombre || selectedProducto.sku}
                        </h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-3">
                            <div>
                              <span className="text-sm text-gray-600 dark:text-gray-400">SKU:</span>
                              <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">{selectedProducto.sku}</span>
                            </div>
                            {selectedProducto.cuello && (
                              <div>
                                <span className="text-sm text-gray-600 dark:text-gray-400">{t('neck')}</span>
                                <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">{selectedProducto.cuello}</span>
                              </div>
                            )}
                            {selectedProducto.estilo && (
                              <div className="flex flex-col">
                                <span className="text-sm text-gray-600 dark:text-gray-400">{t('style')}</span>
                                {isMultiValueField('estilo') && renderMultiValueField(selectedProducto.estilo, 'estilo', 'mt-1')}
                                {!isMultiValueField('estilo') && (
                                  <span className="ml-0 mt-1 font-medium text-gray-900 dark:text-gray-100">{selectedProducto.estilo}</span>
                                )}
                              </div>
                            )}
                            {selectedProducto.genero && (
                              <div className="flex flex-col">
                                <span className="text-sm text-gray-600 dark:text-gray-400">Género:</span>
                                {isMultiValueField('genero') && renderMultiValueField(selectedProducto.genero, 'genero', 'mt-1')}
                                {!isMultiValueField('genero') && (
                                  <span className="ml-0 mt-1 font-medium text-gray-900 dark:text-gray-100">{selectedProducto.genero}</span>
                                )}
                              </div>
                            )}
                            <div>
                              <span className="text-sm text-gray-600 dark:text-gray-400">{t('brand')}</span>
                              <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">{selectedProducto.marca}</span>
                            </div>
                            <div>
                              <span className="text-sm text-gray-600 dark:text-gray-400">{t('stock')}</span>
                              <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">{selectedProducto.stock}</span>
                            </div>
                          </div>
                          <div className="space-y-3">
                            {selectedProducto.talle && (
                              <div className="flex flex-col">
                                <span className="text-sm text-gray-600 dark:text-gray-400">{t('size')}</span>
                                {isMultiValueField('talle') && renderMultiValueField(selectedProducto.talle, 'talle', 'mt-1')}
                                {!isMultiValueField('talle') && (
                                  <span className="ml-0 mt-1 font-medium text-gray-900 dark:text-gray-100">{selectedProducto.talle}</span>
                                )}
                              </div>
                            )}
                            <div>
                              <span className="text-sm text-gray-600 dark:text-gray-400">{t('season')}</span>
                              <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">{selectedProducto.temporada}</span>
                            </div>
                            {selectedProducto.tipo && (
                              <div className="flex flex-col">
                                <span className="text-sm text-gray-600 dark:text-gray-400">{t('type')}</span>
                                {isMultiValueField('tipo') && renderMultiValueField(selectedProducto.tipo, 'tipo', 'mt-1')}
                                {!isMultiValueField('tipo') && (
                                  <span className="ml-0 mt-1 font-medium text-gray-900 dark:text-gray-100">{selectedProducto.tipo}</span>
                                )}
                              </div>
                            )}
                            <div>
                              <span className="text-sm text-gray-600 dark:text-gray-400">{t('price')}</span>
                              <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">${selectedProducto.precioVenta}</span>
                            </div>
                            {selectedProducto.variants && selectedProducto.variants.length > 0 && (
                              <div>
                                <span className="text-sm text-gray-600 dark:text-gray-400">{t('colors')}</span>
                                <div className="ml-2 mt-1">
                                  {selectedProducto.variants.map((variant: any, index: number) => (
                                    <span key={index} className="inline-block bg-gray-200 dark:bg-slate-600 text-gray-800 dark:text-gray-200 px-2 py-1 rounded text-xs mr-1 mb-1">
                                      {variant.color || variant.nombre || `Variante ${index + 1}`}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {/* Campos de array adicionales */}
                            {selectedProducto.tallas && Array.isArray(selectedProducto.tallas) && selectedProducto.tallas.length > 0 && (
                              <div>
                                <span className="text-sm text-gray-600 dark:text-gray-400">{t('sizes')}</span>
                                <div className="ml-2 mt-1">
                                  {selectedProducto.tallas.map((talla: string, index: number) => (
                                    <span key={index} className="inline-block bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded text-xs mr-1 mb-1">
                                      {talla}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {selectedProducto.colores && Array.isArray(selectedProducto.colores) && selectedProducto.colores.length > 0 && (
                              <div>
                                <span className="text-sm text-gray-600 dark:text-gray-400">{t('colors')}</span>
                                <div className="ml-2 mt-1">
                                  {selectedProducto.colores.map((color: string, index: number) => (
                                    <span key={index} className="inline-block bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 px-2 py-1 rounded text-xs mr-1 mb-1">
                                      {color}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {selectedProducto.materiales && Array.isArray(selectedProducto.materiales) && selectedProducto.materiales.length > 0 && (
                              <div>
                                <span className="text-sm text-gray-600 dark:text-gray-400">{t('materials')}</span>
                                <div className="ml-2 mt-1">
                                  {selectedProducto.materiales.map((material: string, index: number) => (
                                    <span key={index} className="inline-block bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-2 py-1 rounded text-xs mr-1 mb-1">
                                      {material}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {selectedProducto.caracteristicas && Array.isArray(selectedProducto.caracteristicas) && selectedProducto.caracteristicas.length > 0 && (
                              <div>
                                <span className="text-sm text-gray-600 dark:text-gray-400">{t('characteristics')}</span>
                                <div className="ml-2 mt-1">
                                  {selectedProducto.caracteristicas.map((caracteristica: string, index: number) => (
                                    <span key={index} className="inline-block bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 px-2 py-1 rounded text-xs mr-1 mb-1">
                                      {caracteristica}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Hashtags */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                        Hashtags
                      </h3>
                      
                      {/* Hashtags preestablecidos */}
                      <div className="mb-4">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                          Hashtags Predefinidos
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {predefinedHashtags.map((hashtag) => (
                            <button
                              key={hashtag}
                              onClick={() => handleHashtagAdd(hashtag)}
                              className="px-3 py-1 text-sm bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors duration-200"
                            >
                              {hashtag}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Hashtags guardados por el usuario */}
                      {hashtags.length > 0 && (
                        <div className="mb-4">
                          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                            Mis Hashtags Guardados
                          </label>
                          <div className="flex flex-wrap gap-2">
                            {hashtags.map((hashtag) => (
                              <button
                                key={hashtag}
                                onClick={() => handleHashtagAdd(hashtag)}
                                className="px-3 py-1 text-sm bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300 rounded-lg hover:bg-pink-200 dark:hover:bg-pink-900/50 transition-colors duration-200"
                              >
                                {hashtag}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Hashtags personalizados */}
                      <div className="mb-4">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                          Hashtags Personalizados
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={newHashtag}
                            onChange={(e) => setNewHashtag(e.target.value)}
                            placeholder="Agregar hashtag personalizado"
                            className="flex-1 px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            onKeyPress={(e) => e.key === 'Enter' && handleCustomHashtagAdd()}
                          />
                          <button
                            onClick={handleCustomHashtagAdd}
                            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {/* Hashtags seleccionados */}
                      {customHashtags.length > 0 && (
                        <div>
                          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                            Hashtags Seleccionados
                          </label>
                          <div className="flex flex-wrap gap-2">
                            {customHashtags.map((hashtag) => (
                              <span
                                key={hashtag}
                                className="inline-flex items-center gap-1 px-3 py-1 text-sm bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-lg"
                              >
                                <Hash className="w-3 h-3" />
                                {hashtag}
                                <button
                                  onClick={() => handleHashtagRemove(hashtag)}
                                  className="ml-1 hover:text-primary-900 dark:hover:text-primary-100"
                                >
                                  <XIcon className="w-3 h-3" />
                                </button>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Generar caption con IA */}
                    {selectedProducto && (
                    <div>
                      <button
                          onClick={() => handleGenerateWithAI(selectedProducto)}
                          disabled={generatingAI}
                        className="w-full px-6 py-2 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-lg hover:from-purple-600 hover:to-blue-600 transition-all duration-200 font-medium shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <div className="flex items-center justify-center space-x-2">
                          <Wand2 className="w-5 h-5" />
                          <span>
                            {generatingAI ? 'Generando...' : 'Generar/Regenerar Caption con IA'}
                          </span>
                        </div>
                      </button>
                      
                      {aiGeneratedCaption && (
                        <div className="mt-4 p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-xl">
                          <h4 className="text-sm font-medium text-purple-900 dark:text-purple-100 mb-2">
                            Caption Generado por IA
                          </h4>
                          <p className="text-sm text-purple-700 dark:text-purple-300">
                            {aiGeneratedCaption}
                          </p>
                        </div>
                      )}
                    </div>
                    )}

                    {/* Selección de plataformas */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                        Redes Sociales
                      </h3>
                      
                      {loading ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-600"></div>
                          <span className="ml-3 text-gray-600 dark:text-gray-300">{t('loadingPlatforms')}</span>
                        </div>
                      ) : platforms.length > 0 ? (
                        <div className="space-y-3">
                          {platforms.map((platform) => (
                            <motion.div
                              key={platform.id}
                              className={`p-4 rounded-xl border cursor-pointer transition-all duration-200 ${
                                platform.selected
                                  ? 'border-primary-200 bg-primary-50 dark:bg-primary-900/20 dark:border-primary-800'
                                  : 'border-gray-200 bg-gray-50 dark:bg-slate-800 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600'
                              }`}
                              onClick={() => handlePlatformToggle(platform.id)}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                  <div className="w-10 h-10 bg-gray-200 dark:bg-slate-700 rounded-lg flex items-center justify-center">
                                    <span className="text-xl">
                                      {platform.id === 'instagram' ? '📸' : 
                                       platform.id === 'facebook' ? '📘' : 
                                       platform.id === 'tiktok' ? '🎵' : '🌐'}
                                    </span>
                                  </div>
                                  <div>
                                    <h4 className="font-medium text-gray-900 dark:text-gray-100">
                                      {platform.name}
                                    </h4>
                                    <p className="text-sm text-gray-600 dark:text-gray-300">
                                      Red social
                                    </p>
                                  </div>
                                </div>
                                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                                  platform.selected
                                    ? 'border-primary-500 bg-primary-500'
                                    : 'border-gray-300 dark:border-slate-600'
                                }`}>
                                  {platform.selected && <Check className="w-4 h-4 text-white" />}
                                </div>
                              </div>
                            </motion.div>
                          ))}
                        </div>
                      ) : (
                        <div className="bg-gradient-to-br from-pink-50 to-purple-50 dark:from-pink-900/20 dark:to-purple-900/20 rounded-xl p-8 border border-pink-200 dark:border-pink-800">
                          <div className="text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-pink-500 to-purple-500 rounded-full mb-4">
                              <Globe className="w-8 h-8 text-white" />
                            </div>
                            <h5 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                              Conectá tus redes favoritas
                            </h5>
                            <p className="text-xs text-pink-600 dark:text-pink-400 font-medium mb-4">
                              Conecta tus redes sociales favoritas para publicar automáticamente
                            </p>
                            <button
                              onClick={() => {
                                window.location.href = '/settings';
                              }}
                              className="px-6 py-2 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-lg hover:from-pink-600 hover:to-purple-600 transition-all duration-200 font-medium shadow-md hover:shadow-lg"
                            >
                              Gestionar cuenta
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-slate-700 flex items-center justify-between">
              <div className="text-sm text-gray-600 dark:text-gray-300">
                {selectedPlatformsCount > 0 ? (
                  <span className="text-green-600 dark:text-green-400">
                    {selectedPlatformsCount} red{selectedPlatformsCount !== 1 ? 'es' : ''} seleccionada{selectedPlatformsCount !== 1 ? 's' : ''}
                  </span>
                ) : (
                  <span className="text-orange-600 dark:text-orange-400">
                    Selecciona al menos una red social
                  </span>
                )}
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={onClose}
                  className="px-6 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors duration-200"
                >
                  Cancelar
                </button>
                <button
                    onClick={() => selectedProducto && handlePublish(selectedProducto)}
                    disabled={selectedPlatformsCount === 0 || publishing || !selectedProducto}
                  className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  {publishing ? 'Publicando...' : 'Publicar'}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>

          {/* Modal PublicarConIA */}
          <PublicarConIA
            isOpen={showPublicarConIA}
            onClose={() => setShowPublicarConIA(false)}
            onProductSelect={handleProductSelect}
            selectedProduct={selectedProducto}
            title="Seleccionar Producto para Redes Sociales"
            subtitle="Elige un producto de tu inventario para publicar en tus redes sociales"
            showImages={true}
            showAllFields={true}
            onGenerateWithAI={handleGenerateWithAI}
            onPublish={handlePublish}
          />
        </>
      )}
      <ToastContainer />
    </AnimatePresence>
  );
};

export default MassPublishingSocialModal;